import { useEffect } from 'react';

import { applyDevPermissionGrant } from '@/api/common/auth/devPermissionGrant';
import { useWhoAmI } from '@/api/common/auth/hooks';
import { setTelemetryUser } from '@/lib/telemetry';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAuth as useClerkAuth } from '@clerk/react';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { setClerkAuth, setUser, setLoadingUserData, clearAuth } = useAuthStore();
  // `isPending`, not `isLoading`: `useWhoAmI` gates the query on the store's `isSignedIn`, which
  // this provider itself publishes via `setClerkAuth` below. On the render where Clerk first
  // reports a signed-in user the store has not caught up yet, so the query is still disabled —
  // and a disabled query reports `isLoading: false` with no data. Treating that window as
  // "whoami returned nothing" would call `setUser(null)` and bounce the user to /unauthorized.
  // `isPending` stays true until the query actually settles, covering both windows.
  const { data: whoAmIData, isPending: isWhoAmIPending, error } = useWhoAmI();

  useEffect(() => {
    setClerkAuth(isLoaded, isSignedIn);

    if (!isLoaded) return;

    if (!isSignedIn) {
      clearAuth();
      setTelemetryUser(undefined);
      return;
    }

    if (isWhoAmIPending) {
      setLoadingUserData(true);
      return;
    }

    if (error || !whoAmIData) {
      setUser(null);
      setTelemetryUser(undefined);
    } else {
      // DEV-ONLY: decorate the real whoAmI user with the full HSQE permission set when
      // the double-gated dev grant is active (no-op in every production build). Keeps
      // the real permission path — the store receives real permission strings, not a
      // blanket bypass.
      setUser(applyDevPermissionGrant(whoAmIData.Security.User));
      setTelemetryUser(whoAmIData.Security.User.Email);
    }
  }, [isLoaded, isSignedIn, whoAmIData, isWhoAmIPending, error, setClerkAuth, setUser, setLoadingUserData, clearAuth]);

  return <>{children}</>;
};
